import React, { useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { colors } from '@/constants/colors';
import type { IspInvoiceRow, IspCustomerRow, IspTenant } from '@/api/isp';

/**
 * An invoice a subscriber can be handed on paper.
 *
 * ─── WHY PAPER STILL MATTERS HERE ───
 * A suspended customer has no internet. They cannot open a portal, read an
 * email, or click a link — which is exactly when they most need to know what
 * they owe and where to send it. Paper and a screenshot are the two things that
 * still work, so this page is built to survive both.
 *
 * ─── WHAT IS ON IT, AND WHY ───
 * The operator's name, address and contact number: "who do I call" is the first
 * question somebody with no internet has, and it is not answerable from a
 * number alone. The customer's code, because that is what an operator searches
 * by when the customer rings up. Every line, so a two-line household can see
 * which connection is which. And the amount OUTSTANDING, distinct from the
 * total, because a partly-paid invoice that only shows its total tells somebody
 * to pay twice.
 *
 * ⚠️ NO PAYMENT METHODS ON A PAID INVOICE. Showing where to send money on a
 * receipt invites a second payment.
 */

const peso = (n: number) =>
   `₱${Number(n ?? 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const day = (iso: string) =>
   new Date(iso).toLocaleDateString('en-PH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
   });

interface Props {
   invoice: IspInvoiceRow;
   customer: IspCustomerRow | null;
   tenant: IspTenant | null;
   onClose: () => void;
}

const IspInvoicePrint: React.FC<Props> = ({
   invoice,
   customer,
   tenant,
   onClose,
}) => {
   /**
    * 🔴 NEUTRALISE THE MODAL'S ANCESTORS BEFORE PRINTING.
    *
    * Ray, 2026-09-01: *"i check the invoice print, it didnt show in the native
    * print modal, its empty."* Measured rather than guessed: the prompt shell
    * puts this sheet inside `position: fixed` with `height: 100vh`, and inside
    * a child with `transform: translate(-50%, -50%)`.
    *
    * A transform makes an element the CONTAINING BLOCK for absolutely
    * positioned descendants — so the sheet's own `position: absolute; left: 0;
    * top: 0` resolved against the centred modal, not the page. Between that,
    * the fixed ancestor (which prints only on the first page) and the 100vh
    * clip, the sheet landed off the paper entirely. Hiding everything else had
    * worked perfectly; there was simply nothing left in view.
    *
    * CSS cannot select ancestors, so they are MARKED here and reset in the
    * print block. The class is removed on unmount, so nothing leaks into the
    * rest of the dashboard.
    */
   useEffect(() => {
      const sheet = document.getElementById('isp-invoice-sheet');
      const marked: HTMLElement[] = [];
      for (
         let p = sheet?.parentElement;
         p && p !== document.body;
         p = p.parentElement
      ) {
         p.classList.add('isp-print-ancestor');
         marked.push(p);
      }
      return () => marked.forEach((el) => el.classList.remove('isp-print-ancestor'));
   }, []);

   const unpaid = invoice.outstanding > 0;
   const methods = (tenant?.paymentMethods ?? []).filter((m) => m.active);

   return (
      <>
         <PrintRules />
         <Sheet id="isp-invoice-sheet">
            <Head>
               <div>
                  {tenant?.logoUrl ? (
                     <Logo src={tenant.logoUrl} alt="" />
                  ) : null}
                  <Biz>{tenant?.businessName ?? 'Your ISP'}</Biz>
                  {tenant?.address ? <Line>{tenant.address}</Line> : null}
                  {tenant?.contactNumber ? (
                     <Line>{tenant.contactNumber}</Line>
                  ) : null}
               </div>
               <HeadRight>
                  <DocTitle>{unpaid ? 'INVOICE' : 'RECEIPT'}</DocTitle>
                  <Line>
                     <strong>{invoice.number}</strong>
                  </Line>
                  <Line>
                     {day(invoice.periodStart)} — {day(invoice.periodEnd)}
                  </Line>
               </HeadRight>
            </Head>

            <Parties>
               <Party>
                  <PartyLbl>Billed to</PartyLbl>
                  <PartyName>{customer?.fullName ?? '—'}</PartyName>
                  {/* The code, not the database id: it is what an operator
                      types when this customer telephones them. */}
                  {customer?.customerCode ? (
                     <Line>Account {customer.customerCode}</Line>
                  ) : null}
                  {customer?.address ? <Line>{customer.address}</Line> : null}
                  {customer?.phone ? <Line>{customer.phone}</Line> : null}
               </Party>
               <Party>
                  <PartyLbl>Due</PartyLbl>
                  <PartyName>{day(invoice.dueAt)}</PartyName>
                  {invoice.paidAt ? (
                     <Line>Paid {day(invoice.paidAt)}</Line>
                  ) : null}
               </Party>
            </Parties>

            <Table>
               <thead>
                  <tr>
                     <Th>Connection</Th>
                     <Th $right>Amount</Th>
                  </tr>
               </thead>
               <tbody>
                  {invoice.lines.map((l, i) => (
                     <tr key={i}>
                        <Td>{l.planName}</Td>
                        <Td $right>{peso(l.amount)}</Td>
                     </tr>
                  ))}
               </tbody>
            </Table>

            <Totals>
               <TotalRow>
                  <span>Total</span>
                  <span>{peso(invoice.total)}</span>
               </TotalRow>
               {invoice.paidAmount > 0 && (
                  <TotalRow>
                     <span>Paid</span>
                     <span>−{peso(invoice.paidAmount)}</span>
                  </TotalRow>
               )}
               {/* 🔴 The number that stops somebody paying twice. A part-paid
                   invoice showing only its total says the wrong thing. */}
               <TotalRow $big>
                  <span>{unpaid ? 'Amount due' : 'Paid in full'}</span>
                  <span>{unpaid ? peso(invoice.outstanding) : peso(0)}</span>
               </TotalRow>
            </Totals>

            {unpaid && methods.length > 0 && (
               <Pay>
                  <PayLbl>How to pay</PayLbl>
                  {methods.map((m) => (
                     <PayRow key={m.id}>
                        <PayMain>
                           <strong>{m.accountName}</strong>
                           {m.accountNumber ? ` · ${m.accountNumber}` : ''}
                           {m.instructions ? <Line>{m.instructions}</Line> : null}
                        </PayMain>
                        {/* The QR is the whole point of printing this for a
                            GCash payer — they scan it off the paper. */}
                        {m.qrUrl ? <Qr src={m.qrUrl} alt="" /> : null}
                     </PayRow>
                  ))}
               </Pay>
            )}

            <Foot>
               Questions about this bill? Call{' '}
               {tenant?.contactNumber || 'your provider'}.
            </Foot>
         </Sheet>

         <Actions className="isp-print-hide">
            <Ghost type="button" onClick={onClose}>
               Close
            </Ghost>
            <Primary type="button" onClick={() => window.print()}>
               Print
            </Primary>
         </Actions>
      </>
   );
};

/**
 * Print CSS.
 *
 * Everything on the page is hidden and the invoice alone is un-hidden, rather
 * than trying to hide each piece of dashboard chrome by name — a list of things
 * to hide goes stale the first time somebody adds a sidebar, and the failure is
 * silent until an operator has already printed 40 of them.
 */
const PrintRules = createGlobalStyle`
   @media print {
      body * { visibility: hidden !important; }
      #isp-invoice-sheet, #isp-invoice-sheet * { visibility: visible !important; }

      /* Every ancestor between the sheet and <body>, flattened. Without this
         the modal's fixed positioning and transform keep the sheet off the
         paper however visible it is. */
      .isp-print-ancestor {
         position: static !important;
         transform: none !important;
         overflow: visible !important;
         inset: auto !important;
         width: auto !important;
         max-width: none !important;
         height: auto !important;
         max-height: none !important;
         margin: 0 !important;
         padding: 0 !important;
         border: none !important;
         border-radius: 0 !important;
         box-shadow: none !important;
         background: #fff !important;
         display: block !important;
      }

      #isp-invoice-sheet {
         position: static;
         width: 100%;
         padding: 0;
         border: none;
         color: #000;
         background: #fff;
      }
      .isp-print-hide { display: none !important; }
      @page { margin: 16mm; }
   }
`;

const Sheet = styled.div`
   background: ${colors.WHITE};
   color: inherit;
   padding: 8px 4px;
   font-size: 13px;
   line-height: 1.6;
`;
const Head = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 24px;
   padding-bottom: 14px;
   border-bottom: 2px solid ${colors.GRAY12};
`;
const HeadRight = styled.div`
   text-align: right;
`;
const Logo = styled.img`
   max-height: 48px;
   max-width: 160px;
   width: auto;
   height: auto;
   object-fit: contain;
   display: block;
   margin-bottom: 6px;
`;
const Biz = styled.div`
   font-size: 17px;
   font-weight: 800;
`;
const DocTitle = styled.div`
   font-size: 17px;
   font-weight: 800;
   letter-spacing: 1px;
`;
const Line = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const Parties = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 24px;
   margin: 16px 0;
`;
const Party = styled.div``;
const PartyLbl = styled.div`
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: ${colors.GRAY5};
`;
const PartyName = styled.div`
   font-weight: 700;
`;
const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   margin-top: 8px;
`;
const Th = styled.th<{ $right?: boolean }>`
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: ${colors.GRAY5};
   padding: 6px 0;
   border-bottom: 1px solid ${colors.GRAY12};
`;
const Td = styled.td<{ $right?: boolean }>`
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   padding: 8px 0;
   border-bottom: 1px solid ${colors.GRAY12};
`;
const Totals = styled.div`
   margin: 14px 0 0 auto;
   max-width: 280px;
`;
const TotalRow = styled.div<{ $big?: boolean }>`
   display: flex;
   justify-content: space-between;
   padding: 5px 0;
   font-size: ${({ $big }) => ($big ? '15px' : '13px')};
   font-weight: ${({ $big }) => ($big ? 800 : 500)};
   border-top: ${({ $big }) => ($big ? `2px solid ${colors.GRAY12}` : 'none')};
`;
const Pay = styled.div`
   margin-top: 20px;
   padding-top: 12px;
   border-top: 1px solid ${colors.GRAY12};
`;
const PayLbl = styled.div`
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: ${colors.GRAY5};
   margin-bottom: 6px;
`;
const PayRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 16px;
   padding: 6px 0;
`;
const PayMain = styled.div``;
const Qr = styled.img`
   width: 84px;
   height: 84px;
   object-fit: contain;
`;
const Foot = styled.div`
   margin-top: 20px;
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 16px;
`;
const Primary = styled.button`
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   padding: 9px 18px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   cursor: pointer;
`;
const Ghost = styled.button`
   font-family: inherit;
   font-size: 14px;
   font-weight: 600;
   padding: 9px 16px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: transparent;
   color: inherit;
   cursor: pointer;
`;

export default IspInvoicePrint;
